import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BRAND_SPLASH_GLYPH, BRAND_SPLASH_MARK } from '@app/shared/brand/brand-marks.generated';

/**
 * Where the build's clock starts.
 *
 * `continue` — the curtain is RELIEVING the pre-Angular splash, which started
 * the same build at page load. The timeline is offset by the elapsed page time
 * so every piece picks up exactly where the splash left it. Measured on a warm
 * boot: the handover happens at ~670 ms, with the roof still drawing and the
 * logotype not yet begun; restarting would rewind them, and jumping to the end
 * would make both appear from nowhere.
 *
 * `restart` — nothing has been building, so the clock starts now. That is the
 * case whenever the curtain opens over an app already on screen: signing in,
 * signing up, switching workspace.
 */
export type BrandSplashPhase = 'restart' | 'continue';

/**
 * The brand mark as a loading screen: the symbol assembles under the logotype,
 * over a status line, a progress track and the product's one-line pitch.
 *
 * WHY THE MARKUP IS INJECTED. The geometry has exactly one source
 * (`scripts/branding/build-brand-assets.mjs`, DESIGN.md §14.1), so it arrives
 * here as generated markup rather than as literal `<path>`s in this template.
 * An `<img>` was tried first and rejected on evidence: a browser freezes such
 * an animation on the image's first rasterised frame, which — with
 * `animation-fill-mode: both` — is the empty one. `<object>` animates properly
 * but `firebase.json` sends `object-src 'none'`.
 *
 * WHY ENCAPSULATION IS OFF. Angular stamps its scoping attribute on template
 * nodes, never on nodes that arrive through `[innerHTML]`; scoped selectors
 * would therefore miss every `<rect>` and `<path>` of the mark. Every selector
 * in this component and in the generated partial is nested under `.brand-splash`
 * or `.pp-splash`, so nothing escapes into the rest of the app.
 */
@Component({
  selector: 'app-brand-splash',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div
      class="brand-splash"
      [class.brand-splash--glyph]="variant() === 'glyph'"
      [class.brand-splash--navy]="ink() === 'navy'"
      [class.brand-splash--still]="!breathe()">
      <div class="pp-splash" [style.--pp-t0]="timelineOrigin()" [innerHTML]="markup()"></div>

      @if (variant() === 'lockup') {
        <div class="brand-splash__status">
          <div class="brand-splash__line">
            <span role="status">{{ message() }}</span>
            @if (percent() !== null) {
              <span class="brand-splash__pct">{{ percent() }} %</span>
            }
          </div>

          <div
            class="brand-splash__track"
            [class.brand-splash__track--indeterminate]="percent() === null"
            role="progressbar"
            [attr.aria-label]="message()"
            [attr.aria-valuenow]="percent()"
            [attr.aria-valuemin]="percent() === null ? null : 0"
            [attr.aria-valuemax]="percent() === null ? null : 100">
            <div class="brand-splash__fill" [style.width.%]="percent()"></div>
          </div>

          @if (tagline()) {
            <p class="brand-splash__tagline">{{ tagline() }}</p>
          }
        </div>
      }
    </div>
  `,
  styleUrl: './brand-splash.component.scss'
})
export class BrandSplashComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** `glyph` drops the logotype, for a surface that already sets the word beside it. */
  readonly variant = input<'lockup' | 'glyph'>('lockup');
  readonly phase = input<BrandSplashPhase>('restart');

  /**
   * Which ink the mark is drawn in — DESIGN.md §14.3.
   *
   * The generated markup carries the ON-GRADIENT variant, white with a `#E4D3FF`
   * accent, because that is what every curtain in this app sits on. A light
   * surface needs the navy one, and the difference is not cosmetic: white on
   * `--ds-rail` is an invisible logo, not a subtle one.
   */
  readonly ink = input<'gradient' | 'navy'>('gradient');

  /**
   * Whether the accent bar keeps breathing once the build has played.
   *
   * The loop says "something is still happening", which is true of a wait and
   * false of a permanent chrome. A nav rail that pulses forever is the product
   * claiming to work when it is idle, so surfaces that merely SHOW the mark turn
   * it off.
   */
  readonly breathe = input(true);

  /**
   * Read ONCE, at construction — which is when this curtain mounts. Reading it
   * per change detection would drag the animation's origin forward on every
   * tick and freeze the build on its first frame.
   */
  private readonly mountedAt = performance.now();

  /**
   * The negative offset that rewinds the animation's clock to page load, so a
   * curtain relieving the splash resumes its build rather than restarting or
   * skipping it. Every delay in the generated stylesheet is
   * `calc(var(--pp-t0) + …)`.
   */
  readonly timelineOrigin = computed(() =>
    this.phase() === 'continue' ? `-${Math.round(this.mountedAt)}ms` : '0ms'
  );

  /** Already translated by the caller — this component owns no copy. */
  readonly message = input<string>('');
  readonly tagline = input<string | null>(null);

  /**
   * Real progress, or `null` when nothing here knows how far along the wait is.
   * `null` shows an indeterminate track rather than a made-up percentage:
   * a bar that invents a number is the product writing something false
   * (DESIGN.md §13).
   */
  readonly progress = input<number | null>(null);

  // Trusted once, not per change detection: this is generated markup from our
  // own build, and re-sanitising the wordmark's 3.4 kB path on every tick would
  // be pure waste.
  private readonly lockupHtml = this.sanitizer.bypassSecurityTrustHtml(BRAND_SPLASH_MARK);
  private readonly glyphHtml = this.sanitizer.bypassSecurityTrustHtml(BRAND_SPLASH_GLYPH);

  readonly markup = computed(() =>
    this.variant() === 'glyph' ? this.glyphHtml : this.lockupHtml
  );

  readonly percent = computed(() => {
    const p = this.progress();
    return p === null || p === undefined ? null : Math.round(Math.min(100, Math.max(0, p)));
  });
}
